import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { colors } from '@constants/colors';
import { getFilters } from '@selectors/data-table-selector';
import { isCurrentFilter } from '@utils/filter-utils';
import SortIconButton from './sort-icon-button';
import './header.scss';

const Header = ({ columns, dataType, isFixed, subType }) => {
  const filters = useSelector(state => getFilters(state, {
    columns,
    dataType,
    isFixed,
    subType
  }));

  const getColumnWidth = width => ({
    maxWidth: `${width}rem`,
    minWidth: `${width}rem`,
    width: `${width}rem`
  });

  const getColumnStyle = column => {
    const baseStyle = getColumnWidth(column.width);
    if (column.ordering && filters) {
      const { ordering } = column;
      if (isCurrentFilter(filters, ordering)) {
        return {
          ...baseStyle,
          backgroundColor: '#E4E4E4'
        };
      }
    }
    return baseStyle;
  };

  return (
    <thead data-testid="datatable-header" styleName="table-header">
      <tr>
        {columns.map(column => {
          const { styleName } = column;
          const isRight = styleName && styleName.includes('right');
          // If we are not sorting, use a hidden SortIcon to maintain space,
          // since if it's the first column, height might be misaligned, since
          // the first column is fixed (i.e. it doesn't scroll).
          const sortIcon = column.ordering ?
            <SortIconButton column={column} dataType={dataType} subType={subType} /> :
            <SortIconButton hidden />;
          return (
            <th key={column.key}
              scope="col"
              data-testid="data-table-header-column-container"
              style={getColumnStyle(column)}
              styleName={`column-container ${styleName ? styleName : ''} ${isFixed ? 'column-fixed' : ''}`}
              // Some columns (row select checkbox, row actions) have no visible label, since
              // it's implied by their icon-only cells - a <th> needs some accessible name
              // regardless, so fall back to one here instead of leaving it empty.
              aria-label={!column.label ? column.ariaLabel : null}
            >
              <div styleName="column-wrapper">
                {isRight && sortIcon}
                <span style={{ color: colors.opacity.black0_8 }}>{column.label}</span>
                {!isRight && sortIcon}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

Header.propTypes = {
  columns: PropTypes.array.isRequired,
  dataType: PropTypes.string.isRequired,
  isFixed: PropTypes.bool,
  subType: PropTypes.string
};

export default memo(Header);
